import React from 'react'
import { IoEye } from 'react-icons/io5'
import { useParams } from 'react-router-dom'
import MatchDetails from './MatchDetails'
import { formatArenaPlacement, formatChampionDisplayName } from '../helper_functions/utilities'
import { createMatchEntryViewModel, getCompactGameModeLabel } from './match/matchViewModels'
import {
    AugmentGrid,
    ChampionAvatar,
    getToneStyles,
    KdaValue,
    EloBadge,
    PerformanceRankBadge,
    renderHistoryEloLabelContent,
    RuneIcons,
    SpellIcons,
    SquareAsset,
    ToneBadge,
} from './match/MatchSharedElements'

const NEUTRAL_BADGE_CLASS_NAME = 'inline-flex h-[20px] shrink-0 items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/65 px-1.5 text-[8.5px] font-bold uppercase tracking-[0.1em] leading-none text-zinc-500'
const MOBILE_CARD_CHROME_INNER_CLASS_NAME = 'px-2.5 py-1.5'
const MOBILE_PILL_CLASS_NAME = 'flex h-[22px] min-w-0 items-center justify-between gap-2 rounded-full border border-zinc-800 bg-zinc-900/80 px-2 text-[10px] leading-none text-zinc-300'

function MetaBadge({ children }) {
    return <div className={NEUTRAL_BADGE_CLASS_NAME}>{children}</div>
}

function formatHistoryChampionDisplayName(championName) {
    if (String(championName || '').trim() === 'ShadowAssassin') {
        return 'S. Assassin'
    }

    return formatChampionDisplayName(championName)
}

function MobileInlineMetric({ compactValue = null, compactValueNode = null, hideLabelOnCompact = false, icon = '', iconClassName = 'h-3.5 w-3.5', iconNode = null, iconStyle = undefined, label, value, valueNode = null }) {
    const hasCompactValue = compactValue !== null || compactValueNode !== null
    const labelClassName = hideLabelOnCompact ? 'truncate text-[9px] font-semibold uppercase leading-none tracking-[0.12em] text-zinc-300 max-lg:hidden' : 'truncate text-[9px] font-semibold uppercase leading-none tracking-[0.12em] text-zinc-300'
    const valueClassName = hasCompactValue ? 'shrink-0 text-right font-semibold leading-none text-zinc-100 max-lg:hidden' : 'shrink-0 text-right font-semibold leading-none text-zinc-100'

    return (
        <div className={MOBILE_PILL_CLASS_NAME}>
            <div className="flex min-w-0 items-center gap-1 leading-none">
                {iconNode || (icon ? <img alt={`${label} icon`} className={`${iconClassName} shrink-0 filter brightness-0 invert`} src={icon} style={iconStyle} /> : null)}
                <span className={labelClassName}>{label}</span>
            </div>
            {valueNode ? <span className={valueClassName}>{valueNode}</span> : <span className={valueClassName}>{value}</span>}
            {hasCompactValue ? (
                compactValueNode ? (
                    <span className="hidden shrink-0 text-right font-semibold leading-none text-zinc-100 max-lg:inline">{compactValueNode}</span>
                ) : (
                    <span className="hidden shrink-0 text-right font-semibold leading-none text-zinc-100 max-lg:inline">{compactValue}</span>
                )
            ) : null}
        </div>
    )
}

function MobilePerformanceBadge({ highestStreak }) {
    if (!highestStreak) {
        return null
    }

    const styleMap = {
        penta: 'text-teal-200 bg-teal-900/20 border border-teal-700/40',
        quadra: 'text-emerald-200 bg-emerald-900/20 border border-emerald-700/40',
    }

    return (
        <div className={`inline-flex h-[22px] w-full items-center justify-center rounded-full px-2 text-center text-[8.5px] font-semibold uppercase tracking-[0.12em] leading-none whitespace-nowrap ${styleMap[highestStreak.style]}`}>
            {highestStreak.label}
        </div>
    )
}

function MobileItemRows({ player, tone }) {
    const itemSources = [
        ...(player.itemSources || []).slice(0, 6),
        player.trinketSource,
        player.roleBoundItemSource,
    ]
    const topRowSources = itemSources.slice(0, 4)
    const bottomRowSources = itemSources.slice(4, 8)

    return (
        <div className="grid h-full w-[74px] grid-rows-2">
            <div className="grid items-center">
                <div className="grid grid-cols-4 gap-0.5">
                    {topRowSources.map((src, index) => (
                        <SquareAsset
                            alt="item icon"
                            className="rounded-md"
                            key={`${src}-${index}`}
                            size={17}
                            src={src}
                            tone={tone}
                        />
                    ))}
                </div>
            </div>
            <div className="grid items-center">
                <div className="grid grid-cols-4 gap-0.5">
                    {bottomRowSources.map((src, index) => (
                        <SquareAsset
                            alt="item icon"
                            className="rounded-md"
                            key={`${src}-${index + 4}`}
                            size={17}
                            src={src}
                            tone={tone}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

function MobileSplitDamageBars({ player, tone }) {
    const toneStyles = getToneStyles(tone)

    return (
        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            <div className="flex min-w-0 items-center gap-1">
                <img
                    alt="damage dealt icon"
                    className="h-4 w-4 shrink-0 filter brightness-0 invert"
                    src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-sword-icon.svg`}
                />
                <div className={`relative h-4 w-full overflow-hidden rounded-sm border ${toneStyles.barTrack}`}>
                    <div
                        className={`h-full ${toneStyles.barFill}`}
                        style={{ width: `${Math.max(0, Math.min(100, player.damageDealtPercent || 0))}%` }}
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center px-1 text-[10px] font-semibold text-zinc-100">
                        {player.damageDealtLabel}
                    </div>
                </div>
            </div>
            <div className="flex min-w-0 items-center gap-1">
                <img
                    alt="damage taken icon"
                    className="h-4 w-4 shrink-0 filter brightness-0 invert"
                    src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-stat-switcher-shield.svg`}
                />
                <div className={`relative h-4 w-full overflow-hidden rounded-sm border ${toneStyles.barTrack}`}>
                    <div
                        className={`h-full ${toneStyles.barFill}`}
                        style={{ width: `${Math.max(0, Math.min(100, player.damageTakenPercent || 0))}%` }}
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center px-1 text-[10px] font-semibold text-zinc-100">
                        {player.damageTakenLabel}
                    </div>
                </div>
            </div>
        </div>
    )
}

function MobileCombatPill({ player, suffix = '' }) {
    const combatText = `${player.kills} / ${player.deaths} / ${player.assists}`

    return (
        <div className={`${MOBILE_PILL_CLASS_NAME} justify-center gap-1 px-2 text-center font-semibold text-zinc-100`}>
            <img
                alt="combat icon"
                className="h-3 w-3 shrink-0 filter brightness-0 invert"
                src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`}
            />
            <span className="min-w-0 truncate leading-none">{combatText}{suffix}</span>
        </div>
    )
}

function MobileArenaTeammatePill({ participant, tone }) {
    const toneStyles = getToneStyles(tone)

    if (!participant) {
        return <div className={MOBILE_PILL_CLASS_NAME} />
    }

    return (
        <div className={`${MOBILE_PILL_CLASS_NAME} border ${toneStyles.rowSurface} justify-start px-2`}>
            <span className="min-w-0 truncate text-[10.5px] font-semibold leading-none text-zinc-100">
                {participant.gameName}
                <span className="ml-1 text-zinc-400">#{participant.tagLine}</span>
            </span>
        </div>
    )
}

function calculateTimeAgo(matchRecord) {
    const timestamp = Number(matchRecord?.archived_match_json?.info?.gameEndTimestamp || 0)

    if (!timestamp) {
        return ': Unknown.'
    }

    const differenceInMinutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000))

    if (differenceInMinutes < 60) {
        return `${differenceInMinutes} minute(s) ago`
    }

    const differenceInHours = Math.floor(differenceInMinutes / 60)

    if (differenceInHours < 24) {
        return `${differenceInHours} hour(s) ago`
    }

    return `${Math.floor(differenceInHours / 24)} day(s) ago`
}

function calculateKda(participant) {
    return (
        <div className="flex items-center gap-1">
            <KdaValue
                className="font-semibold"
                iconSize={14}
                isPerfectKda={Boolean(participant?.challenges?.perfectGame) && Number(participant?.challenges?.kda || 0) !== 0}
                value={participant?.challenges?.kda || 0}
            />
            <span>KDA</span>
        </div>
    )
}

function formatKdaValue(player) {
    if (player.isPerfectKda) {
        return 'Perfect'
    }

    return Number(player.kda || 0).toFixed(1).replace(/\.0$/, '')
}

function formatKdaWithParticipation(player, isArena) {
    const kdaValue = formatKdaValue(player)

    if (isArena) {
        return kdaValue
    }

    return `${kdaValue} (${player.killParticipation ?? 0}%)`
}

function formatCompactGoldValue(player) {
    return player.goldLabel
}

const MatchHistory = ({ orderedChampions, matchHistory, setSummonerData, summonerData }) => {
    const { displayRegion } = useParams()
    const matchEntries = (matchHistory || [])
        .map((matchRecord) => ({
            entry: createMatchEntryViewModel(matchRecord, summonerData?.puuid),
            matchRecord,
        }))
        .filter(({ entry }) => Boolean(entry))

    function navAndSearchParticipant() {
        setSummonerData({})
    }

    if (!matchEntries.length) {
        const hasUnavailableArchives = (matchHistory || []).some((matchRecord) => matchRecord?.archive_unavailable)
        return (
            <div className="rounded-lg py-3 text-white">
                <div className="flex items-center justify-center">
                    <div className="rounded-md border border-amber-400/50 bg-amber-300/10 px-3 py-3 text-sm font-medium text-amber-100">
                        {hasUnavailableArchives ? 'Match history not available. Try updating.' : 'No games played. Try updating.'}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="rounded-lg text-white">
            {matchEntries.map(({ entry, matchRecord }) => {
                const toneStyles = getToneStyles(entry.tone)
                const individualStats = entry.playerRaw
                const compactGameModeLabel = getCompactGameModeLabel(entry.gameModeLabel)
                const currentArenaTeam = entry.isTriosArena
                    ? entry.arenaTeams.find((team) => team.subteamId === entry.player.playerSubteamId)
                    : null
                const teammateParticipants = currentArenaTeam
                    ? currentArenaTeam.participants.filter((participant) => participant.puuid !== entry.player.puuid)
                    : []

                return (
                    <div
                        className={`relative mb-2 overflow-hidden rounded-xl border bg-zinc-950 shadow-[0_14px_34px_-26px_rgba(0,0,0,0.9)] ${toneStyles.mutedBorder}`}
                        key={entry.id}
                    >
                        <div className={`pointer-events-none absolute inset-y-0 left-0 w-[7px] rounded-l-lg ${toneStyles.accentRail}`} />
                        <div className={`pointer-events-none absolute inset-y-0 left-0 w-[3px] rounded-l-lg ${toneStyles.accentStripe}`} />

                        <div className={`overflow-hidden border-b border-zinc-800/70 ${MOBILE_CARD_CHROME_INNER_CLASS_NAME}`}>
                            <div className="flex min-w-0 items-center justify-between gap-1.5">
                                <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
                                    <ToneBadge className="h-[20px] shrink-0 !px-1.5 !text-[8.5px] !tracking-[0.1em]" compact label={entry.statusLabel} tone={entry.tone} />
                                    <PerformanceRankBadge
                                        className="h-[20px] w-[40px] shrink-0 text-[8px]"
                                        rank={entry.player.performanceRank}
                                    />
                                    {entry.player.roleLabel ? (
                                        <MetaBadge>
                                            <img alt={`${entry.player.roleLabel} role icon`} className="h-3 w-3 opacity-85" src={entry.player.roleIconSrc} />
                                            <span>{entry.player.roleLabel}</span>
                                        </MetaBadge>
                                    ) : null}
                                    {entry.player.placementLabel ? (
                                        <ToneBadge className="max-[360px]:hidden h-[20px] shrink-0 !px-1.5 !text-[8.5px] !tracking-[0.1em]" compact label={entry.player.placementLabel} tone={entry.tone} withDot={false} />
                                    ) : null}
                                    <div className="flex h-[20px] min-w-[72px] max-w-[100px] flex-[1_1_84px] shrink-0 items-center overflow-hidden">
                                        <EloBadge
                                            badge={entry.averageEloBadge}
                                            className="h-[20px] min-w-0 text-[9px]"
                                            contentClassName="whitespace-nowrap text-[9px]"
                                            iconClassName="mr-0.5 h-[17px] w-[17px]"
                                            label={renderHistoryEloLabelContent(entry.averageEloDetails)}
                                            showIcon
                                        />
                                    </div>
                                </div>
                                <div className="shrink-0 text-right text-[9px] italic leading-tight text-zinc-300">
                                    <div className="text-zinc-300">{entry.durationLabel}</div>
                                </div>
                            </div>
                        </div>

                        <div className="px-2.5 py-2">
                            <div className={`grid grid-rows-[17px_22px_22px_auto] gap-x-1.5 gap-y-1.5 ${entry.isTriosArena ? 'grid-cols-[112px_minmax(0,1fr)_74px]' : 'grid-cols-[90px_minmax(0,1fr)_74px]'}`}>
                                <div className={`row-span-2 grid h-[45px] w-full grid-rows-[20px_20px] gap-y-[5px] ${entry.isTriosArena ? 'grid-cols-[45px_64px] gap-x-[3px]' : 'grid-cols-[45px_42px] gap-x-[3px]'}`}>
                                    <div className="row-span-2 flex items-start justify-center pt-0.5">
                                        <ChampionAvatar
                                            championIconSrc={entry.player.championIconSrc}
                                            championLevel={entry.player.championLevel}
                                            imageSize={45}
                                            levelBadgeSize={18}
                                            tone={entry.player.tone}
                                        />
                                    </div>
                                    {entry.isTriosArena ? (
                                        <div className="row-span-2 flex items-start overflow-visible pt-0.5">
                                            <AugmentGrid className="gap-[2px]" columns={3} size={20} sources={entry.player.augmentSources} tone={entry.player.tone} />
                                        </div>
                                    ) : entry.isArena ? (
                                        <div className="row-span-2 flex items-center">
                                            <AugmentGrid columns={2} size={16} sources={entry.player.augmentSources.slice(0, 4)} tone={entry.player.tone} />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center">
                                                <SpellIcons className="h-5 w-[42px] items-center justify-between overflow-hidden" size={20} sources={entry.player.spellSources} tone={entry.player.tone} />
                                            </div>
                                            <div className="flex items-center">
                                                <RuneIcons
                                                    className="h-5 w-[42px] items-center justify-between overflow-hidden"
                                                    primaryClassName="scale-[1.04] object-cover"
                                                    primarySize={20}
                                                    secondaryClassName="scale-[0.88] object-contain"
                                                    secondaryInsetClassName=""
                                                    secondarySize={20}
                                                    sources={entry.player.runeSources}
                                                    tone={entry.player.tone}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className={`col-start-2 row-start-1 flex min-w-0 min-h-[22px] items-center gap-1.5 self-center px-2 text-[9.5px] font-semibold leading-none text-zinc-300 ${entry.isTriosArena ? 'col-span-2 pr-0.5' : ''}`}>
                                    <span className="truncate italic uppercase tracking-[0.08em]">{compactGameModeLabel}</span>
                                    <span className="shrink-0 text-zinc-300">•</span>
                                    <span className="truncate text-[10px] text-zinc-300">{formatHistoryChampionDisplayName(entry.player.championName)}</span>
                                </div>

                                {!entry.isTriosArena ? (
                                <div className="col-start-2 row-start-2 grid grid-cols-2 items-center gap-1.5">
                                    <MobileInlineMetric
                                        compactValue={formatKdaValue(entry.player)}
                                        hideLabelOnCompact
                                        icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-sword-icon.svg`}
                                        label="KDA"
                                        value={formatKdaWithParticipation(entry.player, entry.isArena)}
                                    />
                                    {!entry.isArena ? (
                                        <MobileInlineMetric
                                            compactValue={entry.player.csPerMinute.toFixed(1)}
                                            hideLabelOnCompact
                                            icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/minions-slain.svg`}
                                            label="CS"
                                            value={`${entry.player.totalCs} · ${entry.player.csPerMinute.toFixed(1)}`}
                                        />
                                    ) : (
                                        <MobileInlineMetric
                                            iconNode={<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400" />}
                                            label="Place"
                                            value={entry.player.placementLabel || '-'}
                                        />
                                    )}
                                </div>
                                ) : (
                                <div className="col-start-2 row-start-2">
                                    <MobileArenaTeammatePill participant={teammateParticipants[0]} tone={entry.player.tone} />
                                </div>
                                )}

                                {!entry.isTriosArena ? (
                                <div className="col-start-3 row-start-1 flex items-center">
                                    {entry.player.highestStreak ? <MobilePerformanceBadge highestStreak={entry.player.highestStreak} /> : <div className="h-[22px] w-full" />}
                                </div>
                                ) : null}

                                <div className="col-start-3 row-start-2 row-span-2 flex h-full items-stretch">
                                    <MobileItemRows player={entry.player} tone={entry.player.tone} />
                                </div>

                                <div className="col-start-1 row-start-3">
                                    <MobileCombatPill player={entry.player} suffix={entry.isTriosArena ? ` (${formatKdaValue(entry.player)})` : ''} />
                                </div>

                                {!entry.isTriosArena ? (
                                <div className="col-start-2 row-start-3 grid grid-cols-2 items-center gap-1.5">
                                    <MobileInlineMetric
                                        compactValue={formatCompactGoldValue(entry.player)}
                                        hideLabelOnCompact
                                        icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-coins-icon.svg`}
                                        iconClassName="h-3.5 w-3.5"
                                        label="Gold"
                                        value={!entry.isArena ? `${entry.player.goldLabel} · ${Math.round(entry.player.goldPerMinute)}` : entry.player.goldLabel}
                                    />
                                    <MobileInlineMetric
                                        compactValue={entry.player.visionScore}
                                        hideLabelOnCompact
                                        iconNode={<span className="inline-flex items-center"><IoEye className="h-3 w-3 shrink-0 text-zinc-100" /></span>}
                                        label="Vision"
                                        value={entry.player.visionScore}
                                    />
                                </div>
                                ) : (
                                <div className="col-start-2 row-start-3">
                                    <MobileArenaTeammatePill participant={teammateParticipants[1]} tone={entry.player.tone} />
                                </div>
                                )}

                                <div className="col-span-3 row-start-4">
                                    <MobileSplitDamageBars player={entry.player} tone={entry.player.tone} />
                                </div>
                            </div>
                        </div>

                        <MatchDetails
                            calculateKda={calculateKda}
                            calculateTimeAgo={calculateTimeAgo}
                            displayRegion={displayRegion}
                            formatArenaPlacement={formatArenaPlacement}
                            individualStats={individualStats}
                            matchRecord={matchRecord}
                            navAndSearchParticipant={navAndSearchParticipant}
                            orderedChampions={orderedChampions}
                            summonerData={summonerData}
                        />
                    </div>
                )
            })}
        </div>
    )
}

export default MatchHistory
